import React, { useContext, useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  listBlockedContacts,
  unblockBlockedContact
} from "../../services/blockedContactsService";

const reducer = (state, action) => {
  if (action.type === "LOAD_BLOCKS") {
    const rows = action.payload;
    const newRows = [];

    rows.forEach((row) => {
      const index = state.findIndex((item) => item.id === row.id);
      if (index !== -1) {
        state[index] = row;
      } else {
        newRows.push(row);
      }
    });

    return [...state, ...newRows];
  }

  if (action.type === "DELETE_BLOCK") {
    return state.filter((item) => item.id !== action.payload);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap"
  },
  tableHeader: {
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : "#f5f5f5",
    "& th": {
      fontWeight: 700
    }
  },
  sourceChip: {
    fontWeight: 600
  },
  actionsCell: {
    whiteSpace: "nowrap"
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  headerNote: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  unblockButton: {
    background: "linear-gradient(45deg, #2e7d32 30%, #43a047 90%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)"
    }
  }
}));

const BlockedContacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [blockedContacts, dispatch] = useReducer(reducer, []);
  const [totalCount, setTotalCount] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [unblocking, setUnblocking] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.profile !== "admin" && !user.super) {
      toast.error("Apenas administradores podem acessar os bloqueados.");
      history.push("/");
    }
  }, [user, history]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    if (!user || (user.profile !== "admin" && !user.super)) return;

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await listBlockedContacts({
          searchParam,
          pageNumber
        });

        dispatch({ type: "LOAD_BLOCKS", payload: data.records || [] });
        setHasMore(!!data.hasMore);
        setTotalCount(Number(data.count || 0));
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchParam, pageNumber, user]);

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const openUnblockDialog = (blocked) => {
    setSelectedBlock(blocked);
    setConfirmOpen(true);
  };

  const closeUnblockDialog = () => {
    setConfirmOpen(false);
    setSelectedBlock(null);
  };

  const confirmUnblock = async () => {
    if (!selectedBlock) return;

    setUnblocking(true);
    try {
      await unblockBlockedContact(selectedBlock.id);
      dispatch({ type: "DELETE_BLOCK", payload: selectedBlock.id });
      setTotalCount((prev) => Math.max(0, prev - 1));
      toast.success("Contato desbloqueado com sucesso.");
      closeUnblockDialog();
    } catch (err) {
      toastError(err);
    } finally {
      setUnblocking(false);
    }
  };

  const getSourceChipColor = (source) => {
    if (source === "manual") return { backgroundColor: "#e3f2fd", color: "#1565c0" };
    if (source === "campanha_oficial") return { backgroundColor: "#f3e5f5", color: "#6a1b9a" };
    return { backgroundColor: "#eceff1", color: "#37474f" };
  };

  return (
    <MainContainer>
      <MainHeader>
        <div>
          <Title>Contatos bloqueados ({totalCount})</Title>
          <Typography className={classes.headerNote} variant="body2">
            Aqui você confere quem bloqueou e pode liberar o contato para novos testes.
          </Typography>
        </div>

        <MainHeaderButtonsWrapper>
          <div className={classes.toolbar}>
            <TextField
              placeholder="Pesquisar por número, motivo ou origem"
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="secondary" />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                dispatch({ type: "RESET" });
                setPageNumber(1);
              }}
            >
              Atualizar
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Contato</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell>Bloqueado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockedContacts.map((blocked) => (
              <TableRow key={blocked.id}>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {blocked.contactName || "-"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {blocked.originType === "lista_meta"
                      ? "Lista Meta"
                      : blocked.originType === "lista_pro"
                      ? "Lista PRO"
                      : blocked.originType === "contato"
                      ? "Contato do sistema"
                      : "Não identificado"}
                  </Typography>
                </TableCell>
                <TableCell>{blocked.number}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={blocked.sourceLabel}
                    className={classes.sourceChip}
                    style={getSourceChipColor(blocked.source)}
                  />
                </TableCell>
                <TableCell>{blocked.reason || "-"}</TableCell>
                <TableCell>{moment(blocked.blockedAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                <TableCell align="center" className={classes.actionsCell}>
                  <Button
                    variant="contained"
                    size="small"
                    className={classes.unblockButton}
                    startIcon={<CheckCircleIcon />}
                    onClick={() => openUnblockDialog(blocked)}
                  >
                    Desbloquear
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {blockedContacts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className={classes.emptyState}>
                    <BlockIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body1" style={{ marginTop: 8 }}>
                      Nenhum contato bloqueado encontrado.
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            )}

            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={confirmOpen} onClose={closeUnblockDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Desbloquear contato</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Você quer liberar este contato para voltar a receber campanhas e cadastros?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {selectedBlock?.contactName || selectedBlock?.number}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {selectedBlock?.number}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUnblockDialog} disabled={unblocking}>
            Cancelar
          </Button>
          <Button
            onClick={confirmUnblock}
            variant="contained"
            className={classes.unblockButton}
            disabled={unblocking}
          >
            {unblocking ? "Desbloqueando..." : "Confirmar desbloqueio"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default BlockedContacts;
